import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Contrast01Icon} from '@/components/ui/icon/Icon'
import {MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Text} from '@/components/ui/text/Text'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useGetProjectValidators} from '@/features/projects/queries/useGetProjectValidators'
import {useRemoveProjectIncomplete} from '@/features/projects/queries/useRemoveProjectIncomplete'
import {ProjectExtended} from '@/features/projects/types'
import {QUERY_KEYS} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {errorHandler} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import {useQueryClient} from '@tanstack/react-query'

/**
 * Manual removal of the "incomplete" indicator (OPSSTD-4025).
 *
 * SIR-imported projects can land in `progressStatus: 'incomplete'` because of an inconsistency in the
 * legacy import, and the flag never decays on its own - not even once the missing data is filled in.
 * This action is the only way out, so the entry is shown whenever the project is flagged incomplete and
 * is only actionable in active mode (`status === 'open'`), mirroring the backend guard.
 *
 * The flag alone isn't enough though: it only makes sense to lift it while the project still has
 * mandatory fields left to fill in (`/validators`). Once every section is complete the action is
 * pointless, so the entry disappears - see `useProjectHasFieldsToCompile`.
 *
 * The menu entry and the dialog are two components sharing one store because the options menu unmounts
 * its content when it hides (`unmountOnHide`): the dialog has to live outside the menu, so selecting the
 * entry can close the menu normally instead of leaving it stacked on top of the dialog overlay.
 */

/**
 * True when at least one `/validators` section still lists fields to compile. The query is shared with
 * the details tabs (same key, so no extra request) and is only enabled for incomplete projects, the only
 * case where this answer is used. While it's in flight the answer is `false`, so the entry appears as
 * soon as the data lands instead of flashing and then disappearing.
 */
const useProjectHasFieldsToCompile = (project: ProjectExtended) => {
    const {data: projectValidators} = useGetProjectValidators({
        urlParams: {projectId: project.id},
        options: {enabled: project.progressStatus === 'incomplete'}
    })

    return Object.values(projectValidators ?? {}).some(section => section.fieldsToCompile.length > 0)
}

export const ProjectOptionRemoveIncompleteMenuItem = ({
    project,
    dialogStore
}: {
    project: ProjectExtended
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()
    const hasFieldsToCompile = useProjectHasFieldsToCompile(project)

    if (project.progressStatus !== 'incomplete' || hasFieldsToCompile) {
        return null
    }

    // Outside active mode the backend rejects the call, so we keep the entry visible (the flag is still
    // there and users need to know it can be lifted) but disabled, with the reason in a tooltip. The
    // span is the hover target: a disabled control can't reliably fire the pointer events Radix needs.
    if (project.status !== 'open') {
        return (
            <Tooltip
                variant="light"
                content={t('projects:remove_incomplete_disabled_tooltip')}
                triggerProps={{asChild: true}}
            >
                <span style={{display: 'block'}}>
                    <MenuItem disabled onClick={e => e.preventDefault()}>
                        <Contrast01Icon />
                        {t('projects:remove_incomplete')}
                    </MenuItem>
                </span>
            </Tooltip>
        )
    }

    return (
        <MenuItem onClick={() => dialogStore.setOpen(true)}>
            <Contrast01Icon />
            {t('projects:remove_incomplete')}
        </MenuItem>
    )
}

export const ProjectOptionRemoveIncompleteDialog = ({
    project,
    dialogStore
}: {
    project: ProjectExtended
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const hasFieldsToCompile = useProjectHasFieldsToCompile(project)

    const {mutate: removeIncomplete, isPending} = useRemoveProjectIncomplete({
        urlParams: {projectId: project.id},
        options: {
            onError: errorHandler,
            onSuccess: async () => {
                // Patch the cached project first so the badge and the menu entry disappear immediately,
                // then invalidate to reconcile with the server (the response is the `small` resource,
                // not the extended one this page reads, so it can't be written to the cache as-is).
                queryClient.setQueriesData<ProjectExtended | undefined>(
                    {queryKey: [QUERY_KEYS.PROJECT, project.id]},
                    old => (old ? {...old, progressStatus: 'to_print'} : old)
                )
                dialogStore.setOpen(false)
                toast.success(t('projects:remove_incomplete_success'))
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENT_PROJECTS]})
            }
        }
    })

    // Mirrors the menu entry: when the action isn't available there is nothing that can open this dialog.
    if (project.progressStatus !== 'incomplete' || project.status !== 'open' || hasFieldsToCompile) {
        return null
    }

    return (
        <ConfirmationDialog
            dialogStore={dialogStore}
            onConfirm={() => removeIncomplete()}
            isConfirmPending={isPending}
            headerSlot={
                <Flexbox direction="column" gap={2} fullWidth>
                    <DialogHeading>{t('projects:remove_incomplete_title')}</DialogHeading>
                    <Flexbox direction="column" gap={1} fullWidth>
                        <Text palette="neutral" shade="950" size="textMd">
                            {t('projects:pri_n', {code: project.code})}
                        </Text>
                        <Text palette="neutral" shade="600" size="textSm">
                            {t('commons:patient_fullname_value', {fullName: project.patient?.fullName})}
                        </Text>
                    </Flexbox>
                </Flexbox>
            }
            contentSlot={
                <Text palette="neutral" shade="950" size="textSm">
                    {t('projects:remove_incomplete_description')}
                </Text>
            }
        />
    )
}
